import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, parseBriefBody, fmtElapsed, fmtWhen,
  type BriefMeta, type Brief, type BriefBody, type Source, type KeyPoint,
} from "./store";
import {
  runResearch, openWebLogin, BrainError, type BrainErrorCode, type ResearchProgress,
} from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { BackupMenu } from "./BackupMenu";
import Chat from "./chat/Chat";
import {
  Search, KeyRound, Wallet, AlertTriangle, RefreshCw, Trash2, Copy, Check, ExternalLink, Camera, X,
} from "lucide-react";
import { readDownscaledPhoto } from "./photo";

const APP_SCHEME = "oriora-scout";
const BRAND = "Oriora Scout";

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

// Source-domain HUE — a stable hue (0–359) per citation domain; the stylesheet renders it via the
// --hue custom property on inline [n] cites, ordinals and bibliography tabs. Colour = evidence.
function domainHue(domain: string): number {
  const t = (domain || "?").replace(/^www\./, "").toLowerCase();
  let h = 0; for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) % 360;
  return h;
}
// During a run the gathered sources' domains aren't known yet — the accumulating dots use a fixed
// pleasant rotation; the real domain hues take over on the brief document.
const RUN_HUES = [148, 12, 205, 262, 38, 95, 320, 60];

/* ── Shared inline art (stroke: currentColor so both themes work) ─────────────── */
function Masthead({ width = 300 }: { width?: number }) {
  return (
    <svg viewBox="0 0 320 20" width={width} height={Math.round(width / 16)} fill="none" stroke="var(--accent)" style={{ maxWidth: "100%" }} aria-hidden>
      <path d="M0 8.5h136M184 8.5h136" strokeWidth="2.2" />
      <path d="M0 14h320" strokeWidth="0.8" opacity="0.55" />
      <path d="M160 2.5 172.5 8.5 160 14.5 147.5 8.5z" fill="var(--accent)" stroke="none" />
      <path d="M141 4.5v8M144.5 6v5M179 4.5v8M175.5 6v5" strokeWidth="1" />
      <path d="M4 4.5v8M9 6v5M14 6.8v3.4M316 4.5v8M311 6v5M306 6.8v3.4" strokeWidth="1" opacity="0.85" />
    </svg>
  );
}
function PlanGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" aria-hidden>
      <path d="M4.5 3.5v17M4.5 6.5h6M4.5 12h6M4.5 17.5h6" />
      <rect x="11.5" y="4.4" width="4.2" height="4.2" fill="currentColor" stroke="none" />
      <rect x="11.5" y="9.9" width="4.2" height="4.2" />
      <rect x="11.5" y="15.4" width="4.2" height="4.2" />
      <path d="M18.2 3.4h1.9v17.2h-1.9" strokeWidth="1" />
    </svg>
  );
}
function GatherGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" aria-hidden>
      <path d="M3.2 8.6 11.9 5l8.9 3.6M3.2 12.2 11.9 8.6l8.9 3.6M3.2 15.8 11.9 12.2l8.9 3.6" />
      <path d="M11.9 15.6v4.9M9.4 18.3 11.9 20.8l2.5-2.5" strokeWidth="1" />
    </svg>
  );
}
function SynthGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" aria-hidden>
      <path d="M2.6 5.4h4.2c2.6 0 2.6 6.6 5.2 6.6M2.6 12h4.2c2.6 0 2.6 0 5.2 0M2.6 18.6h4.2c2.6 0 2.6-6.6 5.2-6.6M14.6 6.6h6.8v10.8h-6.8z" />
      <path d="M16.2 9.6h3.6M16.2 12h3.6M16.2 14.4h2.2" strokeWidth="1" />
    </svg>
  );
}
function EmptyLibraryArt() {
  return (
    <svg viewBox="0 0 168 124" width="112" height="83" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square" aria-hidden>
      <path d="M4 98.5h160" strokeWidth="1.3" />
      <path d="M4 101.5h160" strokeWidth="0.7" opacity="0.5" />
      <path d="M15 22.5h102" />
      <path d="M18 26h96" strokeWidth="0.7" opacity="0.7" />
      <path d="M20 26v66h92V26" />
      <path d="M15 92.5h102" />
      <path d="M18 96h96" strokeWidth="0.8" />
      <rect x="26" y="31" width="24" height="25" />
      <rect x="54" y="31" width="24" height="25" />
      <rect x="82" y="31" width="24" height="25" />
      <rect x="26" y="60" width="24" height="25" />
      <rect x="82" y="60" width="24" height="25" />
      <path d="M34 45.5h8M62 45.5h8M90 45.5h8M34 74.5h8M90 74.5h8" strokeWidth="0.9" />
      <path d="M50 60h32v25H50z" />
      <path d="M50 64.5h32" strokeWidth="0.7" opacity="0.7" />
      <path d="M62 73h8" strokeWidth="0.9" />
      <g transform="rotate(-5 66 52)">
        <rect x="56" y="38" width="21" height="26" />
        <path d="M60 44.5h13M60 49h13M60 53.5h8" strokeWidth="0.7" opacity="0.75" />
      </g>
      <path d="M112 34l5-5M112 44l5-5M112 54l5-5M112 64l5-5" strokeWidth="0.6" opacity="0.55" />
      <path d="M124 96.5 148 76" />
      <path d="M126.5 99 150.5 78.5" />
      <path d="M148 76l4.5-3.8 2.2 2.6-4.2 3.7z" />
      <path d="M129 86h18v10a3 3 0 0 1-3 3h-12a3 3 0 0 1-3-3z" />
      <ellipse cx="138" cy="86" rx="9" ry="2.6" />
      <path d="M133 91.5h5" strokeWidth="0.7" opacity="0.7" />
      <circle cx="138" cy="86" r="1.7" fill="var(--accent)" stroke="none" />
      <path d="M128 40h4M134 47h3M124 53h5" strokeWidth="0.7" opacity="0.4" />
    </svg>
  );
}
function BriefCompleteArt() {
  return (
    <svg viewBox="0 0 168 124" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square" aria-hidden>
      <path d="M26 10h6M26 10v6M142 10h-6M142 10v6M26 114h6M26 114v-6M142 114h-6M142 114v-6" strokeWidth="0.8" opacity="0.6" />
      <path d="M36 14h74l22 21v75H36z" />
      <path d="M110 14v21h22" />
      <path d="M112 20l16 15" strokeWidth="0.6" opacity="0.5" />
      <path d="M46 44h58" strokeWidth="1.6" />
      <path d="M46 47.5h58" strokeWidth="0.7" />
      <path d="M46 55h34" strokeWidth="2.6" />
      <path d="M46 64h70M46 70h74M46 76h64M46 82h72M46 88h50M46 94h68M46 100h40" strokeWidth="0.9" opacity="0.85" />
      <path d="M40 62.5v4" stroke="hsl(148 50% 46%)" strokeWidth="2.4" />
      <path d="M40 74.5v4" stroke="hsl(12 50% 46%)" strokeWidth="2.4" />
      <path d="M40 86.5v4" stroke="hsl(205 50% 46%)" strokeWidth="2.4" />
      <g transform="translate(126 92)">
        <circle r="14" />
        <circle r="10.5" strokeWidth="0.8" opacity="0.8" />
        <path d="M0-14v-4M9.9-9.9l2.8-2.8M14 0h4M9.9 9.9l2.8 2.8M0 14v4M-9.9 9.9l-2.8 2.8M-14 0h-4M-9.9-9.9l-2.8-2.8" strokeWidth="1.1" />
        <path d="M-4.4-5.4h-2.4v10.8h2.4M4.4-5.4h2.4v10.8H4.4" strokeWidth="1.4" />
        <circle r="2.6" fill="var(--accent)" stroke="none" />
        <path d="M-6 12l-7 14 6-2 3 5 5-13" strokeWidth="1" />
        <path d="M7 12.5l8 13.5-6-1.5-2.5 5" strokeWidth="1" />
      </g>
    </svg>
  );
}
const TrashIcon = () => <Trash2 size={15} />;

/**
 * A waypoint — the mark left where something was found, which is what Scout does with a source.
 * Replaces a literal "✳" character in the launcher: not lucide's Sparkles, but the same idea and
 * the same generic sparkle read, which is the shared-fingerprint half of Guideline 4.3(a).
 */
function WaypointMark({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 21s6.6-6.1 6.6-10.4A6.6 6.6 0 0 0 5.4 10.6C5.4 14.9 12 21 12 21z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <circle cx="12" cy="10.4" r="2.2" fill="currentColor" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors are ignored.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account, came back").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-scout://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to oriora-scout://deleted.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      // Local-first: a deleted account also wipes the on-device research history from this machine.
      try { await store.clearHistory(); } catch { /* ignore */ }
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand={BRAND}
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <BootScreen />;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

// ── Boot / splash ───────────────────────────────────────────────────────────────
function BootScreen() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ display: "grid", placeItems: "center", gap: "0.9rem" }}>
        <Masthead width={200} />
        <div className="stamp">Opening {BRAND}…</div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ minHeight: "100dvh", display: "grid", alignContent: "center", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))", maxWidth: 440, margin: "0 auto" }}>
      <div style={{ marginBottom: 22 }}><Masthead width={240} /></div>
      <div className="doc-kicker" style={{ marginBottom: 12 }}><span className="seal" />{BRAND}</div>
      <h1 className="h1" style={{ margin: "0 0 14px", fontSize: "var(--text-3xl)" }}>Research anything, cited</h1>
      <p className="muted" style={{ fontSize: "var(--text-sm)", lineHeight: 1.7, margin: "0 0 26px" }}>
        Ask a question. Scout runs a multi-step research agent and returns a cited brief,
        filed on this device. AI runs on <strong style={{ color: "var(--text-primary)" }}>your own provider key</strong> (BYOK).
      </p>
      <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
        onClick={async () => {
          setBusy(true);
          // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
          try {
            const p = platform();
            if (p === "ios") await signInIos(APP_SCHEME);
            else if (p === "macos") await signInMac(APP_SCHEME);
            // Web: same-origin login. Go to the site login and return to this app's path; the
            // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
            else if (p === "web") window.location.href = "/login?next=/oriora-scout/";
            else await openWebLogin();
          }
          catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
          finally { setBusy(false); }
        }}>
        {busy ? <><Spinner /> Signing in…</> : "Sign in with Oriora"}
      </button>
      <p className="ask-claim" style={{ marginTop: 16 }}><span className="tick">✓</span>Your research lives on this device unless you back it up</p>
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────
// HOME — the desk: the composer IS the home (R1); the run is the visible ritual
// (R3); the brief opens as its own document surface (R2); the assistant is a mode
// on the composer, not a floating pill (R4).
// ─────────────────────────────────────────────────────────────────────────────────
type RunErr = { code: BrainErrorCode | "unknown"; message: string };
type DeskMode = "ask" | "assistant";

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [q, setQ] = useState("");
  const [runningQ, setRunningQ] = useState("");
  const [mode, setMode] = useState<DeskMode>("ask");
  const [history, setHistory] = useState<BriefMeta[]>([]);
  const [histSearch, setHistSearch] = useState("");
  const [loadingHist, setLoadingHist] = useState(true);
  const [histErr, setHistErr] = useState(false);

  // Active run state (the agentic loop).
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<ResearchProgress | null>(null);
  const [runErr, setRunErr] = useState<RunErr | null>(null);

  // The brief currently shown (a fresh result OR a re-opened history item).
  const [openBrief, setOpenBrief] = useState<{ meta: BriefMeta; body: BriefBody } | null>(null);

  const [confirmDelete, setConfirmDelete] = useState<BriefMeta | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Photographed sources the user attached to the next brief (page/report/whiteboard/slide) —
  // downscaled JPEG data-URLs held ON-DEVICE until the run; sent to the brain only as image input
  // for that brief's synthesize step (brain.ts::runResearch), never otherwise.
  const [photoSources, setPhotoSources] = useState<string[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const reloadHistory = useCallback(async (query = "") => {
    setHistErr(false);
    try {
      const rows = query.trim() ? await store.searchHistory(query, 200) : await store.listBriefs(200);
      setHistory(rows);
    } catch {
      setHistErr(true);
    } finally {
      setLoadingHist(false);
    }
  }, []);
  useEffect(() => { void reloadHistory(); }, [reloadHistory]);

  // Debounced on-device history search (local FTS5 — instant, free, no brain call).
  useEffect(() => {
    const t = setTimeout(() => { void reloadHistory(histSearch); }, 160);
    return () => clearTimeout(t);
  }, [histSearch, reloadHistory]);

  // Cancel any in-flight run if the component unmounts.
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const cancelRun = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
    setProgress(null);
  }, []);

  // Attach one or more photographed sources (native "Take Photo / Photo Library" sheet on iOS).
  const onPhotosPicked = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const picked: string[] = [];
    for (const f of Array.from(files)) {
      try { picked.push(await readDownscaledPhoto(f)); } catch { /* skip an unreadable file */ }
    }
    if (picked.length) setPhotoSources((prev) => [...prev, ...picked]);
  }, []);

  const runResearchFlow = useCallback(async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || running) return;
    const attachedPhotos = photoSources; // snapshot — these become sources of THIS brief
    const controller = new AbortController();
    abortRef.current = controller;
    setMode("ask");
    setRunningQ(trimmed);
    setRunning(true); setRunErr(null); setOpenBrief(null);
    setProgress({ phase: "planning", fraction: 0.03, label: "Starting…" });

    // Open the run in on-device history immediately (so a killed app still shows the attempt).
    let id: string;
    try {
      id = await store.beginBrief(trimmed);
    } catch {
      setRunning(false);
      setRunErr({ code: "unknown", message: "Couldn't start — your device storage is unavailable." });
      return;
    }
    void reloadHistory(histSearch); // show the running row immediately

    const started = Date.now();
    try {
      const brief = await runResearch(trimmed, (p) => setProgress(p), controller.signal, attachedPhotos);
      const elapsed = Date.now() - started;
      const key_points_text = brief.key_points.map((k) => k.text).join("\n");
      const saved = await store.completeBrief({
        id,
        summary: brief.summary,
        body_json: JSON.stringify({ summary: brief.summary, key_points: brief.key_points, sources: brief.sources }),
        sources: brief.sources.map((s) => ({ ordinal: s.ordinal, title: s.title, url: s.url, snippet: s.snippet })),
        key_points_text,
        elapsed_ms: elapsed,
      });
      setRunning(false); setProgress(null); setQ(""); setPhotoSources([]);
      abortRef.current = null;
      if (saved) {
        // Re-read the persisted sources so the displayed brief carries the on-device-derived
        // `domain` (same shape a re-opened history item gets).
        const sources = await store.sourcesFor(saved.id);
        setOpenBrief({
          meta: saved,
          body: { summary: brief.summary, key_points: brief.key_points, sources },
        });
      }
      await reloadHistory(histSearch);
    } catch (err) {
      abortRef.current = null;
      setRunning(false); setProgress(null);
      if (err instanceof DOMException && err.name === "AbortError") {
        // User cancelled — mark the run failed (retryable from history), no error banner.
        try { await store.failBrief(id); } catch { /* ignore */ }
        await reloadHistory(histSearch);
        return;
      }
      try { await store.failBrief(id); } catch { /* ignore */ }
      await reloadHistory(histSearch);
      if (err instanceof BrainError) setRunErr({ code: err.code, message: err.message });
      else setRunErr({ code: "unknown", message: "Something went wrong — please try again." });
    }
  }, [running, reloadHistory, histSearch, photoSources]);

  const openHistoryItem = useCallback(async (m: BriefMeta) => {
    if (m.status !== "done") { void runResearchFlow(m.question); return; }
    try {
      const full = await store.getBrief(m.id);
      const body = parseBriefBody(full);
      if (full && body) setOpenBrief({ meta: full, body });
    } catch { /* ignore — leave the list as-is */ }
  }, [runResearchFlow]);

  // Screenshot mode only: auto-open the most recent completed brief ONCE so the cited report is
  // shown (once only — the brief is now a full-screen surface, so re-opening on every close would
  // make the home unreachable in screenshot runs).
  const autoOpened = useRef(false);
  useEffect(() => {
    if (SCREENSHOT && !autoOpened.current && !openBrief && !running) {
      const first = history.find((m) => m.status === "done");
      if (first) { autoOpened.current = true; void openHistoryItem(first); }
    }
  }, [history, openBrief, running, openHistoryItem]);

  async function doDelete(id: string) {
    setConfirmDelete(null);
    if (openBrief?.meta.id === id) setOpenBrief(null);
    try { await store.deleteBrief(id); } catch { /* ignore */ }
    await reloadHistory(histSearch);
  }

  const EXAMPLES = [
    "What's the current scientific consensus on intermittent fasting?",
    "Compare Rust and Go for building CLI tools",
    "How do heat pumps work, and when do they save money?",
  ];

  const disabled = offline;
  const doneCount = history.filter((h) => h.status === "done").length;

  const deleteModal = confirmDelete && (
    <ModalShell stamp="Delete" title="Delete this brief?" onCancel={() => setConfirmDelete(null)}>
      <p className="muted" style={{ fontSize: "var(--text-sm)", margin: "0 0 20px", lineHeight: 1.6 }}>
        This removes the brief and its {confirmDelete.source_count > 0 ? `${confirmDelete.source_count} ` : ""}sources from this device. It can’t be undone.
      </p>
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
        <button className="btn btn-danger" onClick={() => doDelete(confirmDelete.id)}><Trash2 size={14} /> Delete</button>
      </div>
    </ModalShell>
  );

  // R2 — the brief is its OWN full-screen document surface, never a card in the scroll.
  if (openBrief && !running) {
    return (
      <>
        <BriefDoc
          meta={openBrief.meta} body={openBrief.body} briefNo={doneCount}
          onClose={() => setOpenBrief(null)}
          onDelete={() => setConfirmDelete(openBrief.meta)}
          onFollowUp={() => { setOpenBrief(null); setMode("ask"); setTimeout(() => inputRef.current?.focus(), 80); }}
        />
        {deleteModal}
      </>
    );
  }

  return (
    <main style={{
      maxWidth: 760, margin: "0 auto",
      padding: "max(1.25rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      {/* R1 — the desk rail: wordmark stamp + provenance + tools. No icon tile, no h1. */}
      <div className="desk-rail">
        <span className="desk-mark">
          <span className="seal" />Scout
          <span className="sub">{running ? "researching" : mode === "assistant" ? "assistant" : "on this device"}</span>
        </span>
        <span className="desk-tools">
          {running ? (
            <button className="btn-ghost stamp" style={{ letterSpacing: "0.12em" }} onClick={cancelRun}>Stop</button>
          ) : (
            <>
              <BackupMenu
                appName="Scout" fileSlug="scout" appScheme="oriora-scout"
                hasData={history.length > 0} onRestored={() => reloadHistory(histSearch)}
              />
              <AccountMenu email={email} appSlug="oriora-scout" appName="Oriora Scout" onSignedOut={onSignedOut} />
              <AddFundsMenu />
            </>
          )}
        </span>
      </div>

      {offline && !running && (
        <div role="status" className="banner banner-warning" style={{ marginBottom: "1rem" }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>No connection. Scout needs the web to read sources. Your saved briefs still work.</span>
        </div>
      )}

      {/* R3 — the run ritual takes the desk while a brief is being born. */}
      {running && progress ? (
        <>
          <RunView progress={progress} question={runningQ} />
          <p className="caption" style={{ marginTop: 26, paddingTop: 14, borderTop: "1px solid var(--border-hairline)" }}>
            Only your question leaves this device. Your key, your account.
          </p>
        </>
      ) : mode === "assistant" ? (
        <>
          <div className="ask">
            <div className="ask-modes">
              <button className="ask-mode" onClick={() => setMode("ask")}>Ask</button>
              <button className="ask-mode on">Assistant</button>
            </div>
          </div>
          <aside className="sct-sheet open">
            <div className="sct-head"><span>Type to research, search, or tidy up</span></div>
            <Chat onChanged={() => void reloadHistory(histSearch)} />
          </aside>
        </>
      ) : (
        <>
          {/* LOCKED fleet standard — the floating chat launcher is never removed; here it is
              restyled to the desk's grammar and opens the assistant mode. */}
          <button className="sct-launch" onClick={() => setMode("assistant")} aria-label="Open chat assistant">
            <WaypointMark size={15} /> Chat
          </button>
          {/* R1 — the ask IS the home. */}
          <div className="ask">
            <div className="ask-modes">
              <button className="ask-mode on">Ask</button>
              <button className="ask-mode" onClick={() => setMode("assistant")}>Assistant</button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); void runResearchFlow(q); }}>
              <textarea
                ref={inputRef}
                className="ask-field"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                disabled={running}
                rows={2}
                placeholder={disabled ? "Offline — researching paused" : "What do you want to know?"}
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); void runResearchFlow(q); } }}
                aria-label="Research question"
              />
              {/* Photographed sources attached to this brief — thumbnails with remove. */}
              {photoSources.length > 0 && (
                <div className="ask-photos" style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "10px 0 2px" }}>
                  {photoSources.map((src, i) => (
                    <span key={i} style={{ position: "relative", width: 44, height: 44, flexShrink: 0 }}>
                      <img src={src} alt={`Photographed source ${i + 1}`}
                        style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border-hairline)" }} />
                      <button type="button" aria-label={`Remove photographed source ${i + 1}`}
                        onClick={() => setPhotoSources((prev) => prev.filter((_, j) => j !== i))}
                        style={{
                          position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: 999,
                          padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
                          border: "1px solid var(--border-hairline)", background: "var(--bg, #fff)",
                          color: "var(--ink, #1f1b2e)", cursor: "pointer", lineHeight: 1,
                        }}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {/* accept=image/* gives iOS the native "Take Photo / Photo Library" sheet — one button, both. */}
              <input ref={photoInputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files; e.target.value = ""; void onPhotosPicked(f); }} />
              <div className="ask-foot">
                <span className="ask-claim"><span className="tick">✓</span>Cited from real sources · BYOK</span>
                <button type="button" className="btn" disabled={running}
                  onClick={() => photoInputRef.current?.click()}
                  title="Photograph a page, report, whiteboard or slide to use as a source"
                  style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Camera size={15} /> Photo source
                </button>
                <button type="submit" className="btn btn-primary" disabled={disabled || !q.trim()} style={{ flexShrink: 0 }}>
                  Research
                </button>
              </div>
            </form>
          </div>

          {/* Run error (BYOK / funds / unavailable) */}
          {runErr && <RunError err={runErr} onRetry={() => runResearchFlow(q || (history[0]?.question ?? ""))} onDismiss={() => setRunErr(null)} />}

          {/* Zero state: the run drawn idle, so a new user can read what a run is
              — and what is guaranteed at each station — before spending a call. */}
          {history.length === 0 && !histSearch && !loadingHist && !histErr && <EmptyRun />}

          {/* Example questions — ruled prompt lines, only on a clean desk. */}
          {history.length === 0 && !histSearch && !loadingHist && !histErr && (
            <>
              <div className="sec-head"><span className="stamp">Try one</span><span className="sec-rule" /></div>
              <div>
                {EXAMPLES.map((ex, i) => (
                  <button key={ex} type="button" className="chip-example" disabled={disabled}
                    style={i === EXAMPLES.length - 1 ? { borderBottom: "1px solid var(--border-hairline)" } : undefined}
                    onClick={() => { setQ(ex); void runResearchFlow(ex); }}>{ex}</button>
                ))}
              </div>
            </>
          )}

          {/* The library — the on-device record, demoted beneath the ask. */}
          <div className="sec-head">
            <span className="stamp">Library</span>
            <span className="sec-rule" />
            <span className="sec-count">
              {histSearch ? `${history.length} match${history.length === 1 ? "" : "es"}` : `${doneCount} brief${doneCount === 1 ? "" : "s"}`}
            </span>
          </div>

          {(history.length > 0 || histSearch) && (
            <div className="lib-search">
              <span className="ico"><Search size={14} /></span>
              <input value={histSearch} onChange={(e) => setHistSearch(e.target.value)}
                placeholder="Search titles and sources" aria-label="Search research history" />
            </div>
          )}

          {histErr ? (
            <div className="moment">
              <AlertTriangle size={26} color="var(--danger)" />
              <p className="moment-title">Couldn't load the library</p>
              <p className="moment-note">Something went wrong reading your saved research on this device.</p>
              <button className="btn" onClick={() => { setLoadingHist(true); void reloadHistory(histSearch); }}><RefreshCw size={14} /> Retry</button>
            </div>
          ) : loadingHist ? (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 0 }}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="hist-tile" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
                  <div className="skeleton-line" style={{ width: "70%" }} />
                  <div className="skeleton-line" style={{ width: "45%" }} />
                </div>
              ))}
            </div>
          ) : history.length === 0 ? (
            histSearch ? (
              <p className="caption" style={{ padding: "14px 2px" }}>No saved research matches “{histSearch}”.</p>
            ) : (
              <div className="moment" style={{ paddingTop: 10 }}>
                <EmptyLibraryArt />
                <p className="moment-title">Nothing filed yet</p>
                <p className="moment-note">
                  Ask a question. Scout plans the angles, reads the sources, and writes a cited
                  brief — filed here, on this device.
                </p>
              </div>
            )
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 0 }}>
              {history.map((h, i) => (
                <HistoryTile key={h.id} m={h} last={i === history.length - 1}
                  onOpen={() => void openHistoryItem(h)} onDelete={() => setConfirmDelete(h)} />
              ))}
            </div>
          )}
          {histSearch && <p className="caption" style={{ marginTop: 16 }}>Searched on this device.</p>}
        </>
      )}

      {deleteModal}
    </main>
  );
}

// ── The run at rest — the same three stations, idle, shown before any run exists.
//    Nothing here fires, fetches or bills: it is the ritual drawn, not run. Each
//    station carries only what that station actually guarantees in code: angles are
//    emitted before the gather queue starts (brain.ts, planning phase); retrieval is
//    device-side (searchWeb + readResults) rather than model recall; footnote ordinals
//    are filtered to the brief's own source list; an unbacked point renders the
//    "unverified — no source" flag. Nothing stronger than the code — see the
//    ready-state report for the gather fallback that stops us claiming more.
function EmptyRun() {
  return (
    <div className="run run-empty">
      <div className="sec-head" style={{ marginTop: 0 }}>
        <span className="stamp">A run, step by step</span>
        <span className="sec-rule" />
        <span className="run-count">not started</span>
      </div>
      <div className="run-trail" style={{ ["--run-fill" as never]: 0 }}>
        <div className="run-station">
          <span className="run-glyph"><PlanGlyph /></span>
          <span className="run-name">Plan</span>
          <p className="run-note">
            Your question is split into the angles worth checking. You see that list
            before anything is read.
          </p>
        </div>
        <div className="run-station">
          <span className="run-glyph"><GatherGlyph /></span>
          <span className="run-name">Gather</span>
          <p className="run-note">
            Scout does its own reading: it opens the pages here on this device and
            works from what they actually say, not from what a model recalls.
          </p>
          <div className="run-srcs">
            <span className="run-count">no sources yet</span>
          </div>
        </div>
        <div className="run-station">
          <span className="run-glyph"><SynthGlyph /></span>
          <span className="run-name">Synthesize</span>
          <p className="run-note">
            A footnote is kept only if it points at a source listed under the brief.
            A point with nothing behind it is marked “unverified — no source”.
          </p>
        </div>
      </div>
      <p className="run-terms">
        Runs on your own key — you pay per run, no subscription.<br />
        Every brief is filed here, on this device.
      </p>
    </div>
  );
}

// ── R3 · the run ritual: three stations on a filling spine ──────────────────────
function RunView({ progress, question }: { progress: ResearchProgress; question: string }) {
  const pct = Math.round(progress.fraction * 100);
  const phaseIdx = progress.phase === "planning" ? 0 : progress.phase === "gathering" ? 1 : progress.phase === "synthesizing" ? 2 : 3;
  const stamp = phaseIdx === 0 ? "Planning" : phaseIdx === 1 ? "Gathering" : phaseIdx === 2 ? "Writing brief" : "Brief filed";
  const fill = phaseIdx === 0 ? 0.08
    : phaseIdx === 1 ? 0.1 + ((progress.step ?? 0) / Math.max(progress.total ?? 1, 1)) * 0.55
    : phaseIdx === 2 ? 0.86 : 1;
  const stationCls = (idx: number) => (phaseIdx > idx ? "done" : phaseIdx === idx ? "on" : "");
  const srcCount = progress.sourcesFound ?? 0;
  const stepNow = progress.step ?? 0;

  return (
    <div className="run" aria-live="polite">
      <div className="run-head">
        <span className="stamp">{stamp}</span>
        <span className="run-pct tnum">{pct}%</span>
      </div>
      <div className="conf-track" style={{ marginTop: 10 }}><div className="conf-fill" style={{ width: `${pct}%` }} /></div>
      <p className="run-q" style={{ marginTop: 18 }}>{question}</p>
      <div className="run-trail" style={{ ["--run-fill" as never]: fill }}>
        <div className={`run-station ${stationCls(0)}`}>
          <span className="run-glyph"><PlanGlyph /></span>
          <span className="run-name">Plan</span>
          <p className="run-note">
            {phaseIdx === 0 ? progress.label
              : `${progress.subQuestions?.length ?? 0} angle${(progress.subQuestions?.length ?? 0) === 1 ? "" : "s"} worth checking.`}
          </p>
          {progress.subQuestions && progress.subQuestions.length > 0 && (
            <div className="run-sub">
              {progress.subQuestions.map((sq, i) => {
                const done = phaseIdx >= 2 || (i + 1) < stepNow;
                const on = phaseIdx === 1 && (i + 1) === stepNow;
                return (
                  <div key={i} className={`run-subrow ${done ? "done" : on ? "on" : ""}`}>
                    <span className="mark">{done ? "✓" : on ? "◆" : "○"}</span>
                    <span>{sq}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className={`run-station ${stationCls(1)}`}>
          <span className="run-glyph"><GatherGlyph /></span>
          <span className="run-name">Gather</span>
          <p className="run-note">{phaseIdx === 1 ? progress.label : phaseIdx > 1 ? `${srcCount} source${srcCount === 1 ? "" : "s"} read.` : "Not yet."}</p>
          {(phaseIdx >= 1) && (
            <div className="run-srcs">
              {Array.from({ length: srcCount }, (_, k) => (
                <span key={k} className="run-dot tnum" style={{ ["--hue" as never]: RUN_HUES[k % RUN_HUES.length] }}>{k + 1}</span>
              ))}
              <span className="run-count">{srcCount === 0 ? "no sources yet" : `${srcCount} source${srcCount === 1 ? "" : "s"}`}</span>
            </div>
          )}
        </div>
        <div className={`run-station ${stationCls(2)}`}>
          <span className="run-glyph"><SynthGlyph /></span>
          <span className="run-name">Synthesize</span>
          <p className="run-note">{phaseIdx === 2 ? progress.label : phaseIdx === 3 ? "Filed on this device." : "Waiting on sources."}</p>
        </div>
      </div>
    </div>
  );
}

// ── Run error states ────────────────────────────────────────────────────────────
function RunError({ err, onRetry, onDismiss }: { err: RunErr; onRetry: () => void; onDismiss: () => void }) {
  // BYOK gate — Scout is agentic ⇒ BYOK-only. The user must connect their vendor key in Settings.
  if (err.code === "byok_required") {
    return (
      <div className="banner banner-info" style={{ marginTop: "1rem", alignItems: "flex-start" }}>
        <KeyRound size={16} style={{ color: "var(--info)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Connect your AI provider key</div>
          <div className="muted" style={{ fontSize: "var(--text-xs)", marginTop: 2, lineHeight: 1.5 }}>
            Scout runs a research agent, so it uses your own key (BYOK). Add one in your Oriora account
            settings, then try again.
          </div>
        </div>
      </div>
    );
  }
  if (err.code === "insufficient_funds") {
    return (
      <div className="banner banner-accent" style={{ marginTop: "1rem", alignItems: "flex-start" }}>
        <Wallet size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Add funds to keep researching</div>
          <div className="muted" style={{ fontSize: "var(--text-xs)", marginTop: 2 }}>Add funds in your Oriora account, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="banner banner-danger" style={{ marginTop: "1rem" }}>
        <AlertTriangle size={15} style={{ color: "var(--danger)", flexShrink: 0 }} />
        <span style={{ fontSize: "var(--text-sm)" }}>{err.message}</span>
      </div>
    );
  }
  // Unavailable / unknown — recoverable, offer a retry.
  return (
    <div className="banner banner-danger" style={{ marginTop: "1rem", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
      <span className="row" style={{ gap: "0.45rem", minWidth: 0 }}>
        <AlertTriangle size={15} style={{ color: "var(--danger)", flexShrink: 0 }} />
        <span style={{ fontSize: "var(--text-sm)" }}>{err.message}</span>
      </span>
      <span className="row" style={{ gap: "0.4rem", flexShrink: 0 }}>
        <button className="btn btn-sm" onClick={onRetry}><RefreshCw size={12} /> Retry</button>
        <button className="btn-ghost" style={{ fontSize: "var(--text-xs)", padding: "0.3rem 0.5rem" }} onClick={onDismiss}>Dismiss</button>
      </span>
    </div>
  );
}

// ── R2 · the brief document — its own full-screen typeset surface ───────────────
function BriefDoc({ meta, body, briefNo, onClose, onDelete, onFollowUp }: {
  meta: BriefMeta; body: BriefBody; briefNo: number; onClose: () => void; onDelete: () => void; onFollowUp: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const sourceByOrdinal = useMemo(() => {
    const m = new Map<number, Source>();
    for (const s of body.sources) m.set(s.ordinal, s);
    return m;
  }, [body.sources]);

  function copyBrief() {
    const lines: string[] = [meta.question, "", body.summary, ""];
    body.key_points.forEach((k) => {
      // Carry the uncited-claim flag into the copied brief so the honesty survives export.
      const cites = k.source_ordinals.length > 0
        ? k.source_ordinals.map((o) => `[${o}]`).join("")
        : "[unverified — no source]";
      lines.push(`- ${k.text} ${cites}`.trim());
    });
    if (body.sources.length) {
      lines.push("", "Sources:");
      body.sources.forEach((s) => lines.push(`[${s.ordinal}] ${s.title} — ${s.url}`));
    }
    void navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    });
  }

  return (
    <div className="doc" aria-live="polite">
      <div className="doc-bar">
        <button className="btn-ghost stamp" style={{ letterSpacing: "0.12em" }} onClick={onClose}>← Library</button>
        <span style={{ display: "flex", gap: 2 }}>
          <button className="btn-ghost stamp" style={{ letterSpacing: "0.12em" }} onClick={copyBrief}>
            {copied ? <><Check size={13} color="var(--success)" /> Copied</> : <><Copy size={13} /> Copy</>}
          </button>
          <button className="btn-ghost" aria-label="Delete brief" onClick={onDelete}><TrashIcon /></button>
        </span>
      </div>
      <div className="doc-page">
        <div className="doc-masthead">
          <span className="doc-kicker"><span className="seal" />Scout Brief · Nº {String(briefNo).padStart(2, "0")}</span>
          <Masthead width={300} />
        </div>
        <h1 className="doc-title">{meta.question}</h1>
        <div className="doc-meta">
          <span>{fmtWhen(meta.created_at)}</span>
          <span className="sep">·</span>
          <span className="tnum">{body.sources.length} source{body.sources.length === 1 ? "" : "s"}</span>
          {meta.elapsed_ms > 0 && <><span className="sep">·</span><span className="tnum">read in {fmtElapsed(meta.elapsed_ms)}</span></>}
          <span className="sep">·</span>
          <span>on this device</span>
        </div>

        <div className="doc-body">
          <p className="doc-lede">{body.summary}</p>
          <div className="doc-byline"><OrioraBadge /></div>

          {body.key_points.length > 0 && (
            <>
              <div className="stamp">Key points</div>
              <div className="doc-kp">
                {body.key_points.map((k, i) => (
                  <KeyPointRow key={i} kp={k} n={i + 1} sourceByOrdinal={sourceByOrdinal} />
                ))}
              </div>
            </>
          )}

          {body.sources.length > 0 && (
            <>
              <div className="stamp" style={{ marginTop: 26 }}>Sources · {body.sources.length}</div>
              <div style={{ marginTop: 8 }}>
                {body.sources.map((s) => <SourceRow key={s.ordinal} s={s} />)}
              </div>
            </>
          )}

          {/* R2 · the document's foot: colophon first (the dossier ends), then the
              follow-up as a ruled line. Carry-context chip ships later; the action
              returns to the desk with the composer focused. */}
          <div className="doc-foot">
            <div className="doc-colophon">
              <BriefCompleteArt />
              <p>Filed on this device, {fmtWhen(meta.created_at)}.</p>
            </div>
            <button className="followup" onClick={onFollowUp}>
              <span>
                <span className="followup-label">Ask a follow-up</span>
                <span className="followup-carry">Returns to the desk</span>
              </span>
              <span className="followup-arrow">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KeyPointRow({ kp, n, sourceByOrdinal }: { kp: KeyPoint; n: number; sourceByOrdinal: Map<number, Source> }) {
  return (
    <div className="doc-kpitem">
      <span className="doc-kpnum tnum">{String(n).padStart(2, "0")}</span>
      <p className="doc-kptext">
        {kp.text}
        {kp.source_ordinals.length > 0 ? (
          <span className="cite-group">
            {kp.source_ordinals.map((o) => {
              const s = sourceByOrdinal.get(o);
              return (
                <a key={o} className="cite tnum" href={s?.url} title={s ? `${s.title} — ${s.domain}` : `Source ${o}`}
                  style={s ? { ["--hue" as never]: domainHue(s.domain) } : undefined}
                  onClick={(e) => { e.preventDefault(); if (s?.url) void invoke("open_external", { url: s.url }); }}>
                  {o}
                </a>
              );
            })}
          </span>
        ) : (
          // UNCITED-CLAIM FLAG (research-citations fence). After the hallucinated-citation guard in
          // brain.ts drops any dangling ordinals, a key point can end up with NO source backing it.
          // Rather than render it as if it were sourced (the exact "the sources don't support the
          // claim" trust failure the leaders are criticised for), flag it plainly so the reader
          // knows this line is NOT backed by a gathered source and should be verified independently.
          <span className="kp-unverified" role="note"
            title="No gathered source backs this point — verify it independently."
            style={{
              display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6,
              padding: "1px 6px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              color: "#9a6b00", background: "rgba(224,168,0,0.14)", verticalAlign: "middle",
              whiteSpace: "nowrap",
            }}>
            <AlertTriangle size={11} /> unverified — no source
          </span>
        )}
      </p>
    </div>
  );
}

function SourceRow({ s }: { s: Source }) {
  // A photographed source: no link to open — show its thumbnail and label it as attached.
  if (s.photo_ref) {
    return (
      <div className="src-row" style={{ ["--hue" as never]: domainHue(s.domain), cursor: "default" }} title={s.title}>
        <span className="src-ord tnum">{s.ordinal}</span>
        <img src={s.photo_ref} alt={s.title || "Photographed source"}
          style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border-hairline)", flexShrink: 0 }} />
        <span style={{ minWidth: 0 }}>
          <span className="src-title" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title || "Photographed source"}</span>
          <span className="src-domain">Photographed source · on this device</span>
        </span>
        <span className="src-out"><Camera size={13} /></span>
      </div>
    );
  }
  return (
    <button className="src-row" style={{ ["--hue" as never]: domainHue(s.domain) }}
      onClick={() => { if (s.url) void invoke("open_external", { url: s.url }); }}
      title={s.url}>
      <span className="src-ord tnum">{s.ordinal}</span>
      <span style={{ minWidth: 0 }}>
        <span className="src-title" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title || s.domain || s.url}</span>
        <span className="src-domain">{s.domain}</span>
      </span>
      <span className="src-out"><ExternalLink size={13} /></span>
    </button>
  );
}

function HistoryTile({ m, last, onOpen, onDelete }: { m: BriefMeta; last?: boolean; onOpen: () => void; onDelete: () => void }) {
  const failed = m.status === "failed";
  const running = m.status === "running";
  return (
    <div className={`hist-tile ${running ? "running" : ""} ${failed ? "failed" : ""}`}
      style={last ? { borderBottom: "1px solid var(--border-hairline)" } : undefined}>
      <button onClick={onOpen} style={{ flex: 1, minWidth: 0, textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit" }}>
        <span className="hist-title" style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{m.question}</span>
        {running ? (
          <span className="hist-meta" style={{ color: "var(--accent)" }}>Reading sources…</span>
        ) : failed ? (
          <span className="hist-meta">Stopped before finishing · tap to retry</span>
        ) : (
          <span className="hist-meta">
            {fmtWhen(m.created_at)} · {m.source_count} source{m.source_count === 1 ? "" : "s"}{m.elapsed_ms > 0 ? ` · ${fmtElapsed(m.elapsed_ms)}` : ""}
          </span>
        )}
      </button>
      {!running && (
        <button className="btn-ghost" title="Delete brief" aria-label="Delete brief"
          style={{ padding: "0.4rem", color: "var(--text-tertiary)", flexShrink: 0 }} onClick={onDelete}>
          <TrashIcon />
        </button>
      )}
    </div>
  );
}

function ModalShell({ stamp, title, children, onCancel }: { stamp?: string; title: string; children: React.ReactNode; onCancel: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onCancel} className="modal-scrim">
      <div className="modal-sheet" onClick={(e) => e.stopPropagation()}>
        {stamp && <div className="stamp" style={{ marginBottom: 10 }}>{stamp}</div>}
        <p className="h2" style={{ margin: "0 0 10px" }}>{title}</p>
        {children}
      </div>
    </div>
  );
}

// ── Tiny inline spinner (button "Working…" state) ───────────────────────────────
function Spinner() {
  return (
    <span aria-hidden style={{
      width: 13, height: 13, borderRadius: 999, display: "inline-block",
      border: "2px solid currentColor", borderTopColor: "transparent",
      animation: "spin 0.7s linear infinite", verticalAlign: "-1px", marginRight: 2,
    }} />
  );
}
